import type { Metadata } from "@/lib/head";
import { Link } from "@/lib/nav";
import { ChevronRight, HardHat, House } from "lucide-react";

import CustomerScreenShell from "@/components/customer-screen-shell";
import SectionCard from "@buildpy/ui/section-card";
import { Button } from "@buildpy/ui/button";
import { BRAND_NAME } from "@buildpy/ui/brand-paths";
import { loginHref } from "@buildpy/core/modules/onboarding/rules";
import type { CustomerLink } from "@/modules/project/ui/customer-links";
import { STAFF_HOME_PATH } from "@/pages/start/start-target";

export const metadata: Metadata = {
  title: "시작하기",
  robots: { index: false, follow: false },
};

const RecentLinks = ({ links }: { links: readonly CustomerLink[] }) => (
  <div className="flex flex-col gap-y-2" data-testid="start-recent">
    <p className="text-xs text-muted-foreground">최근 연결한 공사</p>
    <ul className="flex flex-col gap-y-2">
      {links.map((link) => (
        <li key={link.token}>
          <Button asChild className="w-full justify-between" variant="card">
            <Link data-testid="start-recent-link" href={link.path}>
              <span className="flex min-w-0 flex-col items-start text-left">
                <span className="max-w-full truncate text-sm text-muted-foreground">{link.orgName || "시공사"}</span>
                <span className="max-w-full truncate font-semibold">{link.customerName ? `${link.customerName} 고객님 댁` : "우리 집 공사"}</span>
              </span>
              <ChevronRight aria-hidden className="text-muted-foreground" />
            </Link>
          </Button>
        </li>
      ))}
    </ul>
  </div>
);

// 앱 첫 화면(로그인 없음) — 세션·기억한 연결 하나는 route 가 먼저 보내고(?pick=1 이면 하나도 여기서 보인다), 여기는 집주인·직원 중 고르는 화면.
const Page = ({ links = [] }: { links?: readonly CustomerLink[] }) => {
  const recent = links.length > 0;
  return (
    <CustomerScreenShell className="pb-safe-8" safeTop>
      <div className="mx-auto flex w-full max-w-md flex-col gap-y-4">
        <div className="flex flex-col gap-y-1">
          <p className="text-sm font-semibold text-primary">{BRAND_NAME}</p>
          <h1 className="text-xl font-semibold">어떻게 시작할까요?</h1>
          <p className="text-sm text-muted-foreground">공사를 맡기신 집주인이면 위, 시공사 직원이면 아래를 눌러 주세요</p>
        </div>

        <SectionCard className="flex flex-col gap-y-3" data-testid="start-customer">
          <div className="flex flex-col gap-y-0.5">
            <p className="flex items-center gap-x-1.5 font-semibold">
              <House aria-hidden className="size-4" />
              공사 중인 집주인
            </p>
            <p className="text-sm text-muted-foreground">시공사가 알려 준 현장 코드로 우리 집 공사를 볼 수 있어요</p>
          </div>
          {recent && <RecentLinks links={links} />}
          <Button asChild size="cta" variant={recent ? "outline" : "primary"}>
            <Link data-testid="start-connect" href="/connect">
              현장 코드로 연결
            </Link>
          </Button>
        </SectionCard>

        <SectionCard className="flex flex-col gap-y-3" data-testid="start-staff">
          <div className="flex flex-col gap-y-0.5">
            <p className="flex items-center gap-x-1.5 font-semibold">
              <HardHat aria-hidden className="size-4" />
              시공사 직원
            </p>
            <p className="text-sm text-muted-foreground">회사 계정으로 로그인하면 오늘 할 일과 현장이 열려요</p>
          </div>
          <Button asChild size="cta" variant="outline">
            <Link data-testid="start-login" href={loginHref(STAFF_HOME_PATH)}>
              직원 로그인
            </Link>
          </Button>
        </SectionCard>
      </div>
    </CustomerScreenShell>
  );
};

export default Page;
